// src/pages/SuperAdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Cookies from 'js-cookie';
import {
  HiOutlineOfficeBuilding,
  HiOutlineUsers,
  HiOutlineClock,
  HiOutlineCheckCircle,
  HiOutlineEye,
  HiOutlineArrowRight,
  HiOutlineRefresh,
  HiOutlineChartBar,
  HiOutlineUserGroup,
  HiOutlineExclamation,
  HiOutlineXCircle
} from 'react-icons/hi';
import { superAdminService } from '../../../superAdminService';
import StatsCard from '../../SuperAdmin/StatsCard';
import Header from '../../SuperAdmin/Header';
import Sidebar from '../../SuperAdmin/Sidebar';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    totalUsers: 0,
    totalColleges: 0
  });
  const [recentPending, setRecentPending] = useState([]);
  const [apiError, setApiError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Toggle sidebar function
  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  useEffect(() => {
    const superToken = Cookies.get('superAdminToken');
    if (!superToken) {
      navigate('/login');
      return;
    }
    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async (showRefreshToast = false) => {
    try {
      setLoading(true);
      setApiError(false);
      
      const [statsRes, pendingRes] = await Promise.allSettled([
        superAdminService.getStats(),
        superAdminService.getPendingApprovals()
      ]);

      // Handle stats response
      if (statsRes.status === 'fulfilled' && statsRes.value?.success) {
        const statsData = statsRes.value.data?.stats || statsRes.value.data || {};
        setStats({
          pending: statsData.pending || 0,
          approved: statsData.approved || 0,
          rejected: statsData.rejected || 0,
          totalUsers: statsData.totalUsers || 0,
          totalColleges: (statsData.pending || 0) + (statsData.approved || 0) + (statsData.rejected || 0)
        });
      }

      // Handle pending response
      if (pendingRes.status === 'fulfilled' && pendingRes.value?.success) {
        const pendingData = pendingRes.value.data || [];
        setRecentPending(Array.isArray(pendingData) ? pendingData.slice(0, 5) : []);
      }

      setLastUpdated(new Date());
      
      if (showRefreshToast) {
        console.log('Dashboard refreshed');
      }

    } catch (error) {
      console.error('Error in fetchDashboardData:', error);
      setApiError(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData(true);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Calculate total colleges
  const totalColleges = stats.pending + stats.approved + stats.rejected;

  // Loading skeleton for stats cards
  const StatsCardSkeleton = () => (
    <div className="bg-white rounded-xl p-4 sm:p-5 md:p-6 shadow-sm border border-gray-200 animate-pulse">
      <div className="flex justify-between items-center">
        <div className="flex-1">
          <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
          <div className="h-8 bg-gray-300 rounded w-16"></div>
        </div>
        <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
      </div>
      <div className="mt-2 h-4 bg-gray-200 rounded w-32"></div>
    </div>
  );

  // Loading skeleton for pending items
  const PendingItemSkeleton = () => (
    <div className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg animate-pulse">
      <div className="flex items-center space-x-3 sm:space-x-4">
        <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
        <div>
          <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-24"></div>
        </div>
      </div>
      <div className="w-16 h-6 bg-gray-200 rounded-full"></div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header title="Dashboard" toggleSidebar={toggleSidebar} />
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6">
              {[1, 2, 3, 4].map(i => <StatsCardSkeleton key={i} />)}
            </div>
            <div className="bg-white rounded-xl p-4 sm:p-6 mb-6">
              <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
              <div className="space-y-3">
                {[1, 2, 3].map(i => <PendingItemSkeleton key={i} />)}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="Dashboard" toggleSidebar={toggleSidebar} />
        
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="p-4 sm:p-6"
          >
            {/* Welcome Header */}
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6"
            >
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
                  Welcome back, Super Admin! 👋
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {new Date().toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                {lastUpdated && (
                  <div className="text-xs text-gray-400 flex items-center gap-1">
                    <HiOutlineRefresh className="w-3 h-3" />
                    Last updated: {formatDate(lastUpdated)}
                  </div>
                )}
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  aria-label="Refresh dashboard"
                >
                  <HiOutlineRefresh className={`w-5 h-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
                </motion.button>
              </div>
            </motion.div>

            {/* Stats Grid */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.1 }
                }
              }}
              className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8"
            >
              <motion.div
                variants={{
                  hidden: { y: 20, opacity: 0 },
                  visible: { y: 0, opacity: 1 }
                }}
              >
                <StatsCard
                  title="Total Colleges"
                  value={totalColleges}
                  icon={HiOutlineOfficeBuilding}
                  color="gray"
                  onClick={() => navigate('/super-admin/colleges')}
                />
              </motion.div>

              <motion.div
                variants={{
                  hidden: { y: 20, opacity: 0 },
                  visible: { y: 0, opacity: 1 }
                }}
              >
                <StatsCard
                  title="Pending"
                  value={stats.pending}
                  icon={HiOutlineClock}
                  color="gray"
                  onClick={() => navigate('/super-admin/pending')}
                />
              </motion.div>

              <motion.div
                variants={{
                  hidden: { y: 20, opacity: 0 },
                  visible: { y: 0, opacity: 1 }
                }}
              >
                <StatsCard
                  title="Approved"
                  value={stats.approved}
                  icon={HiOutlineCheckCircle}
                  color="gray"
                  onClick={() => navigate('/super-admin/colleges?status=approved')}
                />
              </motion.div>

              <motion.div
                variants={{
                  hidden: { y: 20, opacity: 0 },
                  visible: { y: 0, opacity: 1 }
                }}
              >
                <StatsCard
                  title="Total Users"
                  value={stats.totalUsers}
                  icon={HiOutlineUsers}
                  color="gray"
                  onClick={() => navigate('/super-admin/users')}
                />
              </motion.div>
            </motion.div>

            {/* Summary Cards - Now with consistent gray theme */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6"
            >
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <HiOutlineChartBar className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Approval Rate</p>
                    <p className="text-xl font-bold text-gray-800">
                      {stats.approved > 0 ? Math.round((stats.approved / (stats.pending + stats.approved + stats.rejected)) * 100) : 0}%
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <HiOutlineXCircle className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Rejected</p>
                    <p className="text-xl font-bold text-gray-800">{stats.rejected}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <HiOutlineUserGroup className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Users</p>
                    <p className="text-xl font-bold text-gray-800">{stats.totalUsers}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Recent Pending Approvals */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8 border border-gray-200"
            >
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6">
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800">Recent Pending Approvals</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {recentPending.length} pending {recentPending.length === 1 ? 'registration' : 'registrations'}
                  </p>
                </div>
                {recentPending.length > 0 && (
                  <button
                    onClick={() => navigate('/super-admin/pending')}
                    className="text-gray-600 hover:text-gray-800 font-medium flex items-center text-sm sm:text-base group"
                  >
                    View All
                    <HiOutlineArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                )}
              </div>

              <AnimatePresence mode="wait">
                {recentPending.length === 0 ? (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="text-center py-8 sm:py-12"
                  >
                    <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <HiOutlineCheckCircle className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 text-base sm:text-lg font-medium">All caught up!</p>
                    <p className="text-gray-400 text-xs sm:text-sm mt-1">No pending approvals at the moment</p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="list"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-3 sm:space-y-4"
                  >
                    {recentPending.map((college, index) => (
                      <motion.div
                        key={college._id || index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.02, x: 5 }}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-gray-50 to-white rounded-lg sm:rounded-xl hover:shadow-md transition-all cursor-pointer gap-3 border border-gray-200"
                        onClick={() => navigate(`/super-admin/review/${college._id}`)}
                      >
                        <div className="flex items-center space-x-3 sm:space-x-4">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                            <HiOutlineOfficeBuilding className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-gray-800 text-sm sm:text-base truncate">
                              {college.collegeName}
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-500 truncate">{college.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 ml-13 sm:ml-0">
                          <span className="px-2 sm:px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-xs font-medium whitespace-nowrap flex items-center gap-1">
                            <HiOutlineClock className="w-3 h-3" />
                            Pending
                          </span>
                          <HiOutlineArrowRight className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform sm:hidden" />
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mt-8"
            >
              <motion.button
                whileHover={{ y: -4, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/super-admin/pending')}
                className="bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl sm:rounded-2xl p-4 sm:p-6 text-left hover:shadow-xl transition-all border border-gray-700 group"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <HiOutlineClock className="w-8 h-8 sm:w-10 sm:h-10 mb-3 text-gray-300 group-hover:scale-110 transition-transform" />
                    <h3 className="font-semibold text-base sm:text-lg mb-1">Review Pending</h3>
                    <p className="text-gray-300 text-xs sm:text-sm">Approve or reject new college registrations</p>
                  </div>
                  <HiOutlineArrowRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
                </div>
                {stats.pending > 0 && (
                  <div className="mt-3 flex items-center gap-1">
                    <span className="px-2 py-1 bg-gray-600 text-white text-xs rounded-full">
                      {stats.pending} pending
                    </span>
                  </div>
                )}
              </motion.button>

              <motion.button
                whileHover={{ y: -4, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/super-admin/colleges')}
                className="bg-gradient-to-r from-gray-700 to-gray-800 text-white rounded-xl sm:rounded-2xl p-4 sm:p-6 text-left hover:shadow-xl transition-all border border-gray-600 group"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <HiOutlineOfficeBuilding className="w-8 h-8 sm:w-10 sm:h-10 mb-3 text-gray-300 group-hover:scale-110 transition-transform" />
                    <h3 className="font-semibold text-base sm:text-lg mb-1">Manage Colleges</h3>
                    <p className="text-gray-300 text-xs sm:text-sm">View and manage all registered colleges</p>
                  </div>
                  <HiOutlineArrowRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <span className="px-2 py-1 bg-gray-600 text-white text-xs rounded-full">
                    {stats.approved} approved
                  </span>
                </div>
              </motion.button>
            </motion.div>

            {/* Error message if API fails */}
            <AnimatePresence>
              {apiError && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="mt-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg"
                >
                  <div className="flex items-start gap-3">
                    <HiOutlineExclamation className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-red-800">Unable to load data</h4>
                      <p className="text-sm text-red-600 mt-1">
                        Some information couldn't be loaded. Please try refreshing.
                      </p>
                      <button
                        onClick={handleRefresh}
                        className="mt-2 text-sm text-red-700 hover:text-red-800 font-medium underline"
                      >
                        Refresh now
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;